import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import type {
  FpCodegenEvents,
  FpUserCancel,
  SerializableMcpConnection,
} from "../../../schemas/codegen";
import { useInputStore, useMessageStore } from "../../state/stores";
import { QUERY_KEYS } from "./queryKeys";
import { useActiveChatId } from "./useActiveChatId";
import { useActiveWebSocketConnection } from "./useActiveWebSocketConnection";
import { useAttachmentManagement } from "./useAttachmentManagement";
import { useChatCreation } from "./useChatCreation";
import { useMessageCacheSync } from "./useMessageCacheSync";
import { useMessageManagement } from "./useMessageManagement";
import { useSendChatMessage } from "./useSendChatMessage";

/**
 * Hook for WebSocket messaging operations and real-time message handling.
 *
 * Manages the WebSocket connection lifecycle, message handlers, and provides
 * messaging operations like sending messages and cancelling operations.
 *
 * @returns Object containing messaging operations and connection status
 *
 * When to use:
 * - In components that need to send messages, create chats, or cancel operations
 * - When you need access to connection status for UI states
 * - For components that handle user messaging interactions (like chat inputs)
 *
 * Do NOT use for:
 * - Displaying error notifications (use useCurrentWebSocketError instead)
 * - Simple error state access (access messageStore directly)
 */
export function useWebSocketMessaging() {
  const activeChatId = useActiveChatId();
  const sendMessage = useSendChatMessage();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const activeChatIdRef = useRef(activeChatId);

  const {
    addMessage,
    updateMessage,
    appendMessageContent,
    addMessagePart,
    updateMessagePart,
  } = useMessageManagement();
  const { connection, status: connectionStatus } =
    useActiveWebSocketConnection(activeChatId);
  const { createNewChat } = useChatCreation();
  const { refetch: refetchMessages } = useMessageCacheSync(activeChatId);
  const { handleFileCreated, handleFileUpdated, handleVersionCreated } =
    useAttachmentManagement();

  const clearInput = useInputStore((state) => state.clearInput);
  const appendStepLogs = useMessageStore((state) => state.appendStepLogs);
  const setWebSocketError = useMessageStore((state) => state.setWebSocketError);

  const cancelMessage = useCallback(
    (messageId: string) => {
      if (!activeChatId) {
        console.error("No active chat ID for cancelling message: ", messageId);
        return;
      }

      const messageData: FpUserCancel = {
        type: "user.cancel",
        messageId,
      };

      if (connection) {
        connection.send(messageData);
        refetchMessages();
      }
    },
    [activeChatId, connection, refetchMessages],
  );

  const queryClient = useQueryClient();

  // Keep ref updated with current activeChatId
  useEffect(() => {
    activeChatIdRef.current = activeChatId;
  }, [activeChatId]);

  // Setup connection and message handlers when chatId changes
  useEffect(() => {
    if (!connection || !activeChatId) {
      return;
    }

    connection.connect();
    clearInput();

    // Set up message handler
    const handleMessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        const eventData = data as FpCodegenEvents;

        switch (eventData.type) {
          case "agent.message.created": {
            const newMessage = {
              ...eventData.message,
              status:
                eventData.message.sender === "agent"
                  ? eventData.message.status || "processing"
                  : "completed",
            } as const;
            addMessage(newMessage);
            break;
          }
          case "agent.message.updated": {
            updateMessage(eventData.message.id, {
              ...eventData.message,
            });

            const spec = eventData.message.spec;
            // Handle spec updates from message schema
            if (spec) {
              if (spec.content) {
                handleFileUpdated({
                  type: "agent.file.updated",
                  messageId: eventData.message.id,
                  file: spec,
                });
              } else {
                handleFileCreated({
                  type: "agent.file.created",
                  messageId: eventData.message.id,
                  file: spec,
                });
              }
            }
            break;
          }
          case "agent.message.content.appended": {
            appendMessageContent(eventData.messageId, eventData.content);
            break;
          }
          case "agent.file.created": {
            handleFileCreated({
              type: "agent.file.created",
              messageId: eventData.messageId,
              file: eventData.file,
            });
            break;
          }
          case "agent.file.updated": {
            handleFileUpdated({
              type: "agent.file.updated",
              messageId: eventData.messageId,
              file: eventData.file,
            });
            break;
          }
          case "agent.version.created": {
            handleVersionCreated(eventData);
            break;
          }
          case "agent.error": {
            if (eventData.messageId) {
              updateMessage(eventData.messageId, {
                status: "cancelled",
              });
              refetchMessages();
            }
            setWebSocketError(eventData);
            break;
          }

          case "secret.mutated": {
            // Refetch secrets for this chatId
            if (activeChatId) {
              queryClient.refetchQueries({
                queryKey: [QUERY_KEYS.SECRETS, activeChatId],
              });
            }
            break;
          }

          case "mcp.servers.updated": {
            // Invalidate MCP servers for this activeChatId
            if (activeChatId) {
              queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.MCP_SERVERS, activeChatId],
              });
            }
            break;
          }

          case "mcp.server.add.failed": {
            // Add failed server to MCP servers cache so it shows in UI with error immediately
            if (activeChatId) {
              queryClient.setQueryData(
                [QUERY_KEYS.MCP_SERVERS, activeChatId],
                (
                  old: Record<string, SerializableMcpConnection> | undefined,
                ) => {
                  const failedId = `failed-${Date.now()}`;
                  return {
                    ...old,
                    [failedId]: {
                      id: failedId,
                      url: eventData.url,
                      name: eventData.name,
                      connectionState: "failed" as const,
                      error: eventData.error,
                      createdAt: new Date(),
                    },
                  };
                },
              );

              // Then invalidate to get the latest actual state from server
              queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.MCP_SERVERS, activeChatId],
              });
            }
            break;
          }

          case "agent.message.part.added": {
            addMessagePart(
              eventData.messageId,
              eventData.part,
              eventData.index,
            );
            break;
          }

          case "agent.message.part.updated": {
            updateMessagePart(
              eventData.messageId,
              eventData.partId,
              eventData.updates as {
                content?: string;
                status?:
                  | "streaming"
                  | "complete"
                  | "pending"
                  | "executing"
                  | "error";
                result?: unknown;
              },
            );

            // Check if this is a deployment tool that just completed
            if (eventData.updates.status === "complete") {
              // Get the part from message store to check if it's a deploy tool
              const messageStore = useMessageStore.getState();
              const message = messageStore.messages.find(
                (m) => m.id === eventData.messageId,
              );
              const part = message?.parts?.find(
                (p) => p.id === eventData.partId,
              );

              // Only invalidate endpoints cache if this is a deploy tool completion
              if (part?.type === "tool-call" && part.toolName === "deploy") {
                // Clear any existing timeout
                if (timeoutRef.current) {
                  clearTimeout(timeoutRef.current);
                }

                // Wait 10 seconds for Cloudflare worker deployment to propagate
                timeoutRef.current = setTimeout(() => {
                  // Get fresh activeChatId at execution time to avoid stale closure
                  const currentActiveChatId = activeChatIdRef.current;
                  if (currentActiveChatId) {
                    queryClient.refetchQueries({
                      queryKey: [QUERY_KEYS.ENDPOINTS, currentActiveChatId],
                    });
                  }
                  timeoutRef.current = null;
                }, 10000);
              }
            }
            break;
          }

          case "tasks.created": {
            // Invalidate tasks for the specific message and chat
            if (activeChatId) {
              // Also invalidate the general tasks query for this chat
              queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.TASKS, activeChatId],
              });
            }
            break;
          }

          case "task.updated": {
            // Invalidate tasks for the specific message and chat
            if (activeChatId) {
              // Also invalidate the general tasks query for this chat
              queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.TASKS, activeChatId],
              });
            }
            break;
          }

          case "deployment.updated": {
            queryClient.invalidateQueries({
              queryKey: [QUERY_KEYS.DEPLOYMENTS, eventData.deploymentId],
            });
            break;
          }

          case "deployment.log.append": {
            // Directly append logs to the deployment logs store for real-time updates
            appendStepLogs(
              eventData.deploymentId,
              eventData.id,
              eventData.logs,
            );
            break;
          }

          case "runtime.error": {
            console.log("Runtime error received:", eventData.data);
            // TODO: Add UI handling for runtime errors
            // For now, just log the error - implement UI display later
            break;
          }
        }
      } catch (error) {
        console.error("Error processing WebSocket message:", error);

        setWebSocketError({
          type: "agent.error",
          error: error instanceof Error ? error.message : "Unknown error",
          level: "error",
        });
        refetchMessages();
      }
    };

    connection.addMessageHandler(handleMessage);

    return () => {
      connection.removeMessageHandler(handleMessage);
      // Clear any pending timeout to prevent memory leaks
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [
    activeChatId,
    connection,
    handleFileCreated,
    handleFileUpdated,
    handleVersionCreated,
    setWebSocketError,
    refetchMessages,
    addMessage,
    updateMessage,
    appendMessageContent,
    addMessagePart,
    updateMessagePart,
    clearInput,
    queryClient,
    appendStepLogs,
  ]);

  return {
    sendMessage,
    cancelMessage,
    connectionStatus,
    createNewChat,
  };
}
